/**
 * @module lib/sanitizeSymbolCode
 * @summary Compile a custom symbol's draw-code string into a DrawFn.
 *
 * @description
 *   // HAZARD: this uses `new Function(...)` to turn user/AI-authored text into
 *   executable canvas code. In the current static-first build the code only
 *   ever comes from THIS browser's localStorage or the user's own AI prompt, so
 *   the risk is limited to self-inflicted breakage (caught below). When the
 *   future backend lands, custom-symbol code MUST be validated/sandboxed
 *   server-side before it is trusted across users. See docs/TECHNICAL-GUIDE.md.
 *
 *   The compiled function is wrapped so a buggy symbol can never crash the
 *   whole render loop — it fails silently for that one shape.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 4538, 5436.
 */
import type { DrawFn, Shape } from '../types';

/**
 * Turn a draw-code body (referencing `c` and `s`) into a safe DrawFn.
 * Accepts either a bare body or a `function(c,s){...}` wrapper — we always
 * compile the body via `new Function('c','s', body)`.
 */
export function compileSymbolCode(code: string): DrawFn {
  let compiled: ((c: CanvasRenderingContext2D, s: Shape) => void) | null = null;
  try {
    compiled = new Function('c', 's', code) as (c: CanvasRenderingContext2D, s: Shape) => void;
  } catch (err) {
    console.warn('[customSymbol] failed to compile draw code:', err);
    compiled = null;
  }
  // Return a guarded wrapper: a throwing symbol must not break the frame.
  return (c, s) => {
    if (!compiled) return;
    try {
      compiled(c, s);
    } catch {
      /* one bad symbol shouldn't crash the canvas */
    }
  };
}
